import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { trpc } from '../lib/trpc';
import { useProject } from '../context/ProjectContext';
import { UserDrillDownModal } from '../components/UserDrillDownModal';
import { MessageIntrospectionModal, type IntrospectableMessage } from '../components/MessageIntrospectionModal';
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ComposedChart,
  Line,
  Legend,
  PieChart,
  Pie,
} from 'recharts';

/**
 * Format a timestamp to a relative time string (e.g., "2m ago", "1h ago").
 */
function formatRelativeTime(dateString: string | null | undefined): string {
  if (!dateString) return 'Unknown time';

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay === 1) return 'yesterday';
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}

/**
 * Get initials from a name for avatar display.
 */
function getInitials(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

/**
 * Get trend color and background based on trend direction.
 */
function getTrendStyles(trend: 'up' | 'down' | 'neutral', isPositive = true) {
  const upColor = '#5A8A5E';
  const downColor = '#8B5A50';
  const neutralColor = '#9B7B3C';

  if (trend === 'neutral') {
    return { color: neutralColor, background: `${neutralColor}15` };
  }

  // For metrics where "down" is good (like churn), invert the colors
  const isGood = trend === 'up' ? isPositive : !isPositive;
  const color = isGood ? upColor : downColor;
  return { color, background: `${color}15` };
}

/**
 * Activity item type from the API.
 */
type ActivityItem = {
  id: string;
  direction: string | null;
  messageType: string | null;
  content: string | null;
  context?: unknown;
  platform: string | null;
  status: string | null;
  createdAt: string | null;
  user: {
    id: string;
    name: string | null;
    platformUserId: string | null;
    subscriptionStatus: string | null;
  } | null;
};

/**
 * Dashboard page component.
 *
 * Displays aggregated statistics and recent activity from the API.
 * Uses WebSocket subscription for real-time activity feed updates.
 */
/**
 * Connection state for WebSocket subscription.
 * - 'connecting': Initial connection attempt
 * - 'live': Successfully connected and receiving data
 * - 'reconnecting': Lost connection, attempting to reconnect
 * - 'disconnected': Failed to connect after retries
 */
type ConnectionState = 'connecting' | 'live' | 'reconnecting' | 'disconnected';

export function DashboardPage() {
  const { currentBotId, currentBot } = useProject();
  const [activityItems, setActivityItems] = useState<ActivityItem[]>([]);
  const [connectionState, setConnectionState] = useState<ConnectionState>('connecting');
  const [retryCount, setRetryCount] = useState(0);
  const [lastError, setLastError] = useState<string | null>(null);

  // State for User Growth drill-down modal
  const [drillDownModal, setDrillDownModal] = useState<{
    isOpen: boolean;
    date: string;
    type: 'new' | 'churned';
  }>({
    isOpen: false,
    date: '',
    type: 'new',
  });

  // State for Message Introspection modal
  const [introspectionModal, setIntrospectionModal] = useState<{
    isOpen: boolean;
    message: IntrospectableMessage | null;
  }>({
    isOpen: false,
    message: null,
  });

  // Handler for clicking on User Growth chart data points
  const handleUserGrowthClick = useCallback((date: string, type: 'new' | 'churned') => {
    setDrillDownModal({
      isOpen: true,
      date,
      type,
    });
  }, []);

  const closeDrillDownModal = useCallback(() => {
    setDrillDownModal((prev) => ({ ...prev, isOpen: false }));
  }, []);

  // Handler for opening the Message Introspection modal
  const handleActivityClick = useCallback((activity: ActivityItem) => {
    setIntrospectionModal({
      isOpen: true,
      message: {
        id: activity.id,
        direction: activity.direction,
        messageType: activity.messageType,
        content: activity.content,
        createdAt: activity.createdAt,
        context: activity.context,
        user: activity.user,
      },
    });
  }, []);

  const closeIntrospectionModal = useCallback(() => {
    setIntrospectionModal((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const statsQuery = trpc.dashboard.stats.useQuery(
    { botId: currentBotId ?? undefined },
    { enabled: !!currentBotId }
  );

  // Fetch safety alerts stats for notification badges
  const safetyStatsQuery = trpc.safety.stats.useQuery(
    { botId: currentBotId ?? undefined },
    { enabled: !!currentBotId }
  );

  // Initial load of recent activity (for when the page first loads)
  const activityQuery = trpc.dashboard.recentActivity.useQuery(
    { limit: 10, botId: currentBotId ?? undefined },
    { enabled: !!currentBotId }
  );

  // Initialize activity items from initial query
  useEffect(() => {
    if (activityQuery.data) {
      setActivityItems(activityQuery.data);
    }
  }, [activityQuery.data]);

  // Handler for new activity from WebSocket subscription
  const handleNewActivity = useCallback((newActivity: ActivityItem) => {
    setActivityItems((prevItems) => {
      // Avoid duplicates by checking if this activity already exists
      if (prevItems.some((item) => item.id === newActivity.id)) {
        return prevItems;
      }
      // Add new activity to the beginning and keep only the latest 10
      return [newActivity, ...prevItems].slice(0, 10);
    });
  }, []);

  // Reset connection state when bot changes
  useEffect(() => {
    if (currentBotId) {
      setConnectionState('connecting');
      setRetryCount(0);
      setLastError(null);
    }
  }, [currentBotId]);

  // Subscribe to real-time activity stream via WebSocket
  // tRPC handles automatic reconnection; we track state for UI feedback
  trpc.dashboard.recentActivityStream.useSubscription(
    { botId: currentBotId ?? undefined },
    {
      enabled: !!currentBotId,
      onData: (data) => {
        // Successfully received data means connection is live
        if (connectionState !== 'live') {
          setConnectionState('live');
          setRetryCount(0);
          setLastError(null);
        }
        handleNewActivity(data);
      },
      onStarted: () => {
        // Connection established successfully
        setConnectionState('live');
        setRetryCount(0);
        setLastError(null);
      },
      onError: (err) => {
        console.error('Activity stream error:', err);
        setLastError(err.message);

        // Move to reconnecting state (tRPC will auto-retry)
        setConnectionState((prev) => {
          // If we were live, we're now reconnecting
          if (prev === 'live') {
            return 'reconnecting';
          }
          // If we were already reconnecting, increment retry count
          if (prev === 'reconnecting') {
            setRetryCount((count) => count + 1);
          }
          return 'reconnecting';
        });
      },
    }
  );

  if (statsQuery.isLoading) {
    return <div style={styles.loading}>Loading dashboard...</div>;
  }

  if (statsQuery.error) {
    return (
      <div style={styles.error}>
        Error loading dashboard: {statsQuery.error.message}
      </div>
    );
  }

  const stats = statsQuery.data;

  // Calculate AI response time display values
  const avgResponseTimeMs = stats?.avgAiResponseTime?.current ?? 0;
  const prevResponseTimeMs = stats?.avgAiResponseTime?.previous ?? 0;
  const responseTimeCount = stats?.avgAiResponseTime?.count ?? 0;

  // Format response time: show in seconds if >= 1000ms, otherwise in ms
  const formatResponseTime = (ms: number): string => {
    if (ms === 0) return '0ms';
    if (ms >= 1000) {
      return `${(ms / 1000).toFixed(1)}s`;
    }
    return `${Math.round(ms)}ms`;
  };

  // Calculate trend for response time
  const responseTimeTrend = (() => {
    if (prevResponseTimeMs === 0 || avgResponseTimeMs === 0) return 'neutral' as const;
    if (avgResponseTimeMs > prevResponseTimeMs) return 'up' as const;
    if (avgResponseTimeMs < prevResponseTimeMs) return 'down' as const;
    return 'neutral' as const;
  })();

  // Calculate percentage change for response time
  const responseTimeChange = (() => {
    if (prevResponseTimeMs === 0) return '0%';
    const change = ((avgResponseTimeMs - prevResponseTimeMs) / prevResponseTimeMs) * 100;
    const sign = change >= 0 ? '+' : '';
    return `${sign}${Math.round(change)}%`;
  })();

  // Build metrics data from API stats
  const metricsData: MetricCardData[] = [
    {
      label: 'Active Users',
      value: (stats?.totalUsers ?? 0).toLocaleString(),
      change: '+12%',
      trend: 'up' as const,
      subtext: `${stats?.subscriptionBreakdown?.trial ?? 0} in trial`,
      icon: '👥',
      isPositiveGood: true,
    },
    {
      label: 'Active Subscriptions',
      value: (stats?.subscriptionBreakdown?.active ?? 0).toLocaleString(),
      change: '+8%',
      trend: 'up' as const,
      subtext: 'Paid subscribers',
      icon: '💰',
      isPositiveGood: true,
    },
    {
      label: 'Engagement Rate',
      value: '78%',
      change: '+5%',
      trend: 'up' as const,
      subtext: 'Avg response rate',
      icon: '📊',
      isPositiveGood: true,
    },
    {
      label: 'Churn Risk',
      value: (stats?.churnRisk?.total ?? 0).toString(),
      change: stats?.churnRisk?.high ? `-${stats.churnRisk.high}` : '0',
      trend: (stats?.churnRisk?.total ?? 0) > 0 ? 'up' as const : 'down' as const,
      subtext: `${stats?.churnRisk?.high ?? 0} high risk`,
      icon: '⚠️',
      isPositiveGood: false,
    },
    {
      label: 'Messages Today',
      value: (stats?.todayMessages ?? 0).toLocaleString(),
      change: '+15%',
      trend: 'up' as const,
      subtext: 'Bot interactions',
      icon: '💬',
      isPositiveGood: true,
    },
    {
      label: 'Avg AI Response Time',
      value: formatResponseTime(avgResponseTimeMs),
      change: responseTimeChange,
      trend: responseTimeTrend,
      subtext: `${responseTimeCount} responses this week`,
      icon: '⚡',
      isPositiveGood: false, // Lower response time is better (up is bad, down is good)
    },
  ];

  return (
    <div style={styles.container}>
      {/* Dashboard Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Dashboard</h1>
          <div style={styles.headerMeta}>
            {currentBot && (
              <span
                style={{
                  ...styles.projectBadge,
                  background: `${currentBot.color || '#6366f1'}20`,
                  border: `1px solid ${currentBot.color || '#6366f1'}30`,
                }}
              >
                <span>{currentBot.icon || '◐'}</span> {currentBot.name}
              </span>
            )}
            <span style={styles.lastUpdated}>Last updated: Just now</span>
          </div>
        </div>
      </div>

      {/* Key Metrics Row */}
      <div style={styles.metricsGrid}>
        {metricsData.map((metric, index) => (
          <MetricCard key={index} {...metric} />
        ))}
      </div>

      {/* Charts Row */}
      <div style={styles.chartsGrid}>
        <EngagementChart engagementData={stats?.engagementTrend} />
        <RevenueChart botId={currentBotId} />
      </div>

      {/* User Growth & Churn Risk Row */}
      <div style={styles.chartsGrid}>
        <UserGrowthChart
          userGrowthData={stats?.userGrowth}
          onDataPointClick={handleUserGrowthClick}
        />
        <ChurnRiskChart churnRisk={stats?.churnRisk} />
      </div>

      {/* User Growth Drill-down Modal */}
      <UserDrillDownModal
        isOpen={drillDownModal.isOpen}
        onClose={closeDrillDownModal}
        date={drillDownModal.date}
        type={drillDownModal.type}
        botId={currentBotId ?? undefined}
      />

      {/* Message Introspection Modal */}
      <MessageIntrospectionModal
        isOpen={introspectionModal.isOpen}
        onClose={closeIntrospectionModal}
        message={introspectionModal.message}
      />

      {/* Quick Actions Panel */}
      <QuickActionsPanel
        safetyAlertsCount={
          (safetyStatsQuery.data?.byStatus.needsReview ?? 0) +
          (safetyStatsQuery.data?.byStatus.inProgress ?? 0)
        }
        pendingUsersCount={stats?.subscriptionBreakdown?.trial ?? 0}
      />

      {/* Recent Activity */}
      <section style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Live Activity</h2>
          <ConnectionStatusIndicator
            state={connectionState}
            retryCount={retryCount}
            lastError={lastError}
          />
        </div>
        {activityQuery.isLoading && activityItems.length === 0 ? (
          <div style={styles.loading}>Loading activity...</div>
        ) : activityQuery.error && activityItems.length === 0 ? (
          <div style={styles.error}>
            Error loading activity: {activityQuery.error.message}
          </div>
        ) : (
          <div style={styles.activityList}>
            {activityItems.length === 0 ? (
              <div style={styles.empty}>No recent activity</div>
            ) : (
              activityItems.map((activity) => (
                <ActivityItemComponent
                  key={activity.id}
                  activity={activity}
                  onClick={() => handleActivityClick(activity)}
                />
              ))
            )}
          </div>
        )}
      </section>
    </div>
  );
}

interface ActivityItemComponentProps {
  activity: ActivityItem;
  onClick?: () => void;
}

function ActivityItemComponent({ activity, onClick }: ActivityItemComponentProps) {
  const isInbound = activity.direction === 'inbound';
  const userName = activity.user?.name ?? 'Unknown User';
  const initials = getInitials(userName);

  return (
    <div
      style={{
        ...styles.activityItem,
        ...(isInbound ? styles.activityItemInbound : styles.activityItemOutbound),
        ...(onClick ? styles.activityItemClickable : {}),
      }}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); } : undefined}
    >
      {/* Avatar */}
      <div
        style={{
          ...styles.avatar,
          ...(isInbound ? styles.avatarInbound : styles.avatarOutbound),
        }}
      >
        {isInbound ? initials : '🤖'}
      </div>

      {/* Content */}
      <div style={styles.activityContent}>
        <div style={styles.activityHeader}>
          <span style={styles.activityUserName}>{userName}</span>
          <span
            style={{
              ...styles.directionBadge,
              ...(isInbound ? styles.directionBadgeInbound : styles.directionBadgeOutbound),
            }}
          >
            {isInbound ? 'User' : 'Bot'}
          </span>
          <span style={styles.activityTime}>
            {formatRelativeTime(activity.createdAt)}
          </span>
        </div>
        <div style={styles.activityPreview}>{activity.content || ''}</div>
      </div>

      {/* Direction indicator */}
      <div
        style={{
          ...styles.directionIndicator,
          ...(isInbound ? styles.directionIndicatorInbound : styles.directionIndicatorOutbound),
        }}
      >
        {isInbound ? '→' : '←'}
      </div>
    </div>
  );
}

interface ConnectionStatusIndicatorProps {
  state: ConnectionState;
  retryCount: number;
  lastError: string | null;
}

/**
 * Connection status indicator component.
 * Displays the current WebSocket connection state with appropriate styling.
 */
function ConnectionStatusIndicator({ state, retryCount, lastError }: ConnectionStatusIndicatorProps) {
  const getStatusConfig = () => {
    switch (state) {
      case 'live':
        return {
          label: 'Live',
          color: '#10b981',
          dotAnimation: true,
          showRetry: false,
        };
      case 'connecting':
        return {
          label: 'Connecting...',
          color: '#9B9285',
          dotAnimation: false,
          showRetry: false,
        };
      case 'reconnecting':
        return {
          label: retryCount > 0 ? `Reconnecting (${retryCount})...` : 'Reconnecting...',
          color: '#f59e0b',
          dotAnimation: true,
          showRetry: true,
        };
      case 'disconnected':
        return {
          label: 'Disconnected',
          color: '#ef4444',
          dotAnimation: false,
          showRetry: false,
        };
      default:
        return {
          label: 'Unknown',
          color: '#9B9285',
          dotAnimation: false,
          showRetry: false,
        };
    }
  };

  const config = getStatusConfig();

  return (
    <div style={styles.connectionStatusContainer}>
      <span
        style={{
          ...styles.liveIndicator,
          color: config.color,
        }}
      >
        <span
          style={{
            ...styles.liveDot,
            backgroundColor: config.color,
            animation: config.dotAnimation ? 'pulse 2s infinite' : 'none',
          }}
        />
        {config.label}
      </span>
      {state === 'reconnecting' && lastError && (
        <span
          style={styles.connectionErrorHint}
          title={lastError}
        >
          Connection lost
        </span>
      )}
    </div>
  );
}

interface MetricCardData {
  label: string;
  value: string;
  change: string;
  trend: 'up' | 'down' | 'neutral';
  subtext: string;
  icon: string;
  isPositiveGood?: boolean;
}

function MetricCard({
  label,
  value,
  change,
  trend,
  subtext,
  icon,
  isPositiveGood = true,
}: MetricCardData) {
  const trendStyles = getTrendStyles(trend, isPositiveGood);
  const trendArrow = trend === 'up' ? '↑' : trend === 'down' ? '↓' : '';

  return (
    <div style={styles.metricCard}>
      <div style={styles.metricHeader}>
        <span style={styles.metricIcon}>{icon}</span>
        <span
          style={{
            ...styles.trendBadge,
            color: trendStyles.color,
            background: trendStyles.background,
          }}
        >
          {trendArrow} {change}
        </span>
      </div>
      <p style={styles.metricValue}>{value}</p>
      <p style={styles.metricLabel}>{label}</p>
      <p style={styles.metricSubtext}>{subtext}</p>
    </div>
  );
}

interface EngagementChartProps {
  engagementData?: Array<{ day: string; date: string; value: number }>;
}

/**
 * Engagement Trend bar chart component.
 * Displays daily response rate for the week using Recharts BarChart.
 */
function EngagementChart({ engagementData }: EngagementChartProps) {
  const chartColor = '#6366f1';

  // Use real data from the API
  const data = engagementData ?? [];

  // Check if there's any meaningful data (non-zero values)
  const hasData = data.length > 0 && data.some((d) => d.value > 0);

  // Calculate week-over-week improvement
  const currentWeekAvg =
    data.length > 0
      ? Math.round(data.reduce((acc, d) => acc + d.value, 0) / data.length)
      : 0;

  // Custom tooltip component
  const CustomTooltip = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: Array<{ payload: { day: string; date: string; value: number } }>;
  }) => {
    if (active && payload && payload.length > 0) {
      const item = payload[0].payload;
      return (
        <div style={styles.tooltip}>
          <p style={styles.tooltipTitle}>{item.day}</p>
          {item.date && <p style={styles.tooltipDate}>{item.date}</p>}
          <p style={styles.tooltipValue}>{item.value}% response rate</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div style={styles.chartCard}>
      <div style={styles.chartHeader}>
        <div>
          <h3 style={styles.chartTitle}>Engagement Trend</h3>
          <p style={styles.chartSubtitle}>Daily response rate this week</p>
        </div>
        <select style={styles.chartSelect}>
          <option>This Week</option>
        </select>
      </div>
      {/* Recharts Bar Chart */}
      <div style={{ ...styles.barChartContainer, height: '160px' }}>
        {hasData ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 20, right: 10, left: 10, bottom: 5 }}>
              <XAxis
                dataKey="day"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 11, fill: '#9B9285' }}
              />
              <YAxis
                domain={[0, 100]}
                hide
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(99, 102, 241, 0.1)' }} />
              <Bar
                dataKey="value"
                radius={[6, 6, 0, 0]}
                maxBarSize={32}
                label={{
                  position: 'top',
                  fontSize: 11,
                  fontWeight: 600,
                  fill: '#3D3A36',
                  formatter: (value: number) => `${value}%`,
                }}
              >
                {data.map((_, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={`url(#barGradient-${index})`}
                  />
                ))}
              </Bar>
              <defs>
                {data.map((_, index) => (
                  <linearGradient
                    key={`gradient-${index}`}
                    id={`barGradient-${index}`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="0%" stopColor={chartColor} />
                    <stop offset="100%" stopColor={`${chartColor}80`} />
                  </linearGradient>
                ))}
              </defs>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div style={styles.noDataContainer}>
            <span style={{ fontSize: '24px' }}>📊</span>
            <p style={styles.noDataText}>No engagement data available</p>
          </div>
        )}
      </div>
      {hasData && (
        <div style={styles.chartInsight}>
          <span style={styles.chartInsightIcon}>📊</span>
          <p style={styles.chartInsightText}>
            <strong>Average: {currentWeekAvg}%</strong> response rate this week
          </p>
        </div>
      )}
    </div>
  );
}

interface RevenueChartProps {
  botId?: string | null;
}

/**
 * Revenue Growth area chart component.
 * Displays monthly recurring revenue using Recharts AreaChart.
 * Fetches real MRR data from the analytics.revenue API endpoint.
 */
function RevenueChart({ botId }: RevenueChartProps) {
  const chartColor = '#6366f1';

  // Fetch MRR data from tRPC backend - only when botId is available
  const revenueQuery = trpc.analytics.revenue.useQuery(
    { months: 6, botId: botId ?? undefined },
    { enabled: !!botId }
  );

  // Transform API response data for Recharts visualization
  const chartData = revenueQuery.data?.data.map((d) => ({
    month: d.month,
    value: d.mrr,
  })) ?? [];

  // Check if there's any meaningful data to display
  const hasData = chartData.length > 0;

  const currentMrr = revenueQuery.data?.summary.currentMrr ?? 0;
  const previousMrr = revenueQuery.data?.summary.previousMrr ?? 0;
  const mrrChange = currentMrr - previousMrr;
  const isPositiveChange = mrrChange >= 0;

  // Custom tooltip component
  const CustomTooltip = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: Array<{ payload: { month: string; value: number } }>;
  }) => {
    if (active && payload && payload.length > 0) {
      const item = payload[0].payload;
      return (
        <div style={styles.tooltip}>
          <p style={styles.tooltipTitle}>{item.month}</p>
          <p style={styles.tooltipValue}>€{item.value.toLocaleString()} MRR</p>
        </div>
      );
    }
    return null;
  };

  if (revenueQuery.isLoading) {
    return (
      <div style={styles.chartCard}>
        <div style={styles.chartHeader}>
          <div>
            <h3 style={styles.chartTitle}>Revenue Growth</h3>
            <p style={styles.chartSubtitle}>Monthly recurring revenue</p>
          </div>
        </div>
        <div style={{ ...styles.lineChartContainer, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ color: '#9B9285', fontSize: '13px' }}>Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.chartCard}>
      <div style={styles.chartHeader}>
        <div>
          <h3 style={styles.chartTitle}>Revenue Growth</h3>
          <p style={styles.chartSubtitle}>Monthly recurring revenue</p>
        </div>
        {hasData && (
          <div style={{ textAlign: 'right' as const }}>
            <p style={styles.revenueMrr}>€{currentMrr.toLocaleString()}</p>
            <p style={{ ...styles.revenueChange, color: isPositiveChange ? '#5A8A5E' : '#8B5A50' }}>
              {isPositiveChange ? '+' : ''}€{mrrChange.toLocaleString()} this month
            </p>
          </div>
        )}
      </div>
      {/* Recharts Area Chart - renders real MRR data from API */}
      <div style={{ ...styles.lineChartContainer, height: '160px' }}>
        {hasData ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 5 }}>
              <defs>
                <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={chartColor} stopOpacity={0.3} />
                  <stop offset="100%" stopColor={chartColor} stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="month"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 11, fill: '#9B9285' }}
              />
              <YAxis
                hide
                domain={['auto', 'auto']}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="value"
                stroke={chartColor}
                strokeWidth={2.5}
                fill="url(#revenueGradient)"
                dot={{ fill: 'white', stroke: chartColor, strokeWidth: 2, r: 4 }}
                activeDot={{ fill: chartColor, stroke: 'white', strokeWidth: 2, r: 5 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div style={styles.noDataContainer}>
            <span style={{ fontSize: '24px' }}>💰</span>
            <p style={styles.noDataText}>No revenue data available</p>
          </div>
        )}
      </div>
    </div>
  );
}

interface UserGrowthChartProps {
  userGrowthData?: Array<{ day: string; date: string; newUsers: number; churnedUsers: number }>;
  onDataPointClick?: (date: string, type: 'new' | 'churned') => void;
}

/**
 * User Growth combined bar/line chart component.
 * Displays new users (bars) and churned users (line) over time.
 * Supports click events for drill-down functionality.
 */
function UserGrowthChart({ userGrowthData, onDataPointClick }: UserGrowthChartProps) {
  const newUsersColor = '#5A8A5E';
  const churnedUsersColor = '#8B5A50';

  // Use real data from the API
  const data = userGrowthData ?? [];

  // Check if there's any meaningful data
  const hasData = data.length > 0;

  // Calculate totals
  const totalNewUsers = data.reduce((acc, d) => acc + d.newUsers, 0);
  const totalChurnedUsers = data.reduce((acc, d) => acc + d.churnedUsers, 0);
  const netGrowth = totalNewUsers - totalChurnedUsers;

  // Custom tooltip component
  const CustomTooltip = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: Array<{ payload: { day: string; date: string; newUsers: number; churnedUsers: number }; dataKey: string; value: number; color: string }>;
  }) => {
    if (active && payload && payload.length > 0) {
      const item = payload[0].payload;
      return (
        <div style={styles.tooltip}>
          <p style={styles.tooltipTitle}>{item.day}</p>
          {item.date && <p style={styles.tooltipDate}>{item.date}</p>}
          <p style={{ ...styles.tooltipValue, color: newUsersColor }}>
            +{item.newUsers} new users
          </p>
          <p style={{ ...styles.tooltipValue, color: churnedUsersColor }}>
            -{item.churnedUsers} churned
          </p>
          {onDataPointClick && (
            <p style={styles.tooltipHint}>Click to view details</p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div style={styles.chartCard}>
      <div style={styles.chartHeader}>
        <div>
          <h3 style={styles.chartTitle}>User Growth</h3>
          <p style={styles.chartSubtitle}>New signups vs churned users</p>
        </div>
        <select style={styles.chartSelect}>
          <option>This Week</option>
        </select>
      </div>
      {/* Recharts Composed Chart - Bar for new users, Line for churned */}
      <div style={{ ...styles.barChartContainer, height: '160px' }}>
        {hasData ? (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 20, right: 10, left: 10, bottom: 5 }}>
              <XAxis
                dataKey="day"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 11, fill: '#9B9285' }}
              />
              <YAxis hide />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }}
                formatter={(value) => (
                  <span style={{ color: '#5C564E' }}>
                    {value === 'newUsers' ? 'New Users' : 'Churned Users'}
                  </span>
                )}
              />
              <Bar
                dataKey="newUsers"
                name="newUsers"
                fill={newUsersColor}
                radius={[4, 4, 0, 0]}
                maxBarSize={28}
                style={{ cursor: onDataPointClick ? 'pointer' : 'default' }}
                onClick={(barData) => {
                  if (onDataPointClick && barData?.date) {
                    onDataPointClick(barData.date, 'new');
                  }
                }}
              />
              <Line
                type="monotone"
                dataKey="churnedUsers"
                name="churnedUsers"
                stroke={churnedUsersColor}
                strokeWidth={2}
                dot={{ fill: churnedUsersColor, stroke: 'white', strokeWidth: 2, r: 4 }}
                activeDot={{
                  fill: churnedUsersColor,
                  stroke: 'white',
                  strokeWidth: 2,
                  r: 5,
                  style: { cursor: onDataPointClick ? 'pointer' : 'default' },
                  onClick: (_event: unknown, dotData: unknown) => {
                    if (onDataPointClick) {
                      const payload = (dotData as { payload?: { date?: string } })?.payload;
                      if (payload?.date) {
                        onDataPointClick(payload.date, 'churned');
                      }
                    }
                  },
                }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <div style={styles.noDataContainer}>
            <span style={{ fontSize: '24px' }}>📈</span>
            <p style={styles.noDataText}>No user growth data available</p>
          </div>
        )}
      </div>
      {hasData && (
        <div style={{
          ...styles.chartInsight,
          background: netGrowth >= 0 ? 'rgba(90, 138, 94, 0.06)' : 'rgba(139, 90, 80, 0.06)',
        }}>
          <span style={styles.chartInsightIcon}>{netGrowth >= 0 ? '📈' : '📉'}</span>
          <p style={{
            ...styles.chartInsightText,
            color: netGrowth >= 0 ? '#5A8A5E' : '#8B5A50',
          }}>
            <strong>Net growth: {netGrowth >= 0 ? '+' : ''}{netGrowth}</strong> users this week
            ({totalNewUsers} new, {totalChurnedUsers} churned)
          </p>
        </div>
      )}
    </div>
  );
}

interface QuickActionsPanelProps {
  safetyAlertsCount?: number;
  pendingUsersCount?: number;
}

/**
 * Quick Actions Panel component.
 * Provides navigation buttons to common admin tasks with optional notification badges.
 */
function QuickActionsPanel({ safetyAlertsCount, pendingUsersCount }: QuickActionsPanelProps) {
  const navigate = useNavigate();

  const quickActions = [
    {
      label: 'Safety Alerts',
      description: 'Review flagged content',
      icon: '🛡️',
      route: '/safety',
      color: '#8B5A50',
      badge: safetyAlertsCount,
    },
    {
      label: 'Pending Activations',
      description: 'Manage user signups',
      icon: '👥',
      route: '/users',
      color: '#6366f1',
      badge: pendingUsersCount,
    },
    {
      label: 'Support Queue',
      description: 'Handle support requests',
      icon: '💬',
      route: '/support',
      color: '#5A8A5E',
      badge: undefined,
    },
    {
      label: 'Settings',
      description: 'Configure system',
      icon: '⚙️',
      route: '/settings',
      color: '#9B7B3C',
      badge: undefined,
    },
  ];

  return (
    <div style={styles.quickActionsCard}>
      <div style={styles.quickActionsHeader}>
        <h3 style={styles.quickActionsTitle}>Quick Actions</h3>
        <p style={styles.quickActionsSubtitle}>Jump to common tasks</p>
      </div>
      <div style={styles.quickActionsGrid}>
        {quickActions.map((action) => (
          <button
            key={action.route}
            onClick={() => navigate(action.route)}
            style={styles.quickActionButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <span
              style={{
                ...styles.quickActionIcon,
                background: `${action.color}15`,
                position: 'relative' as const,
              }}
            >
              {action.icon}
              {action.badge !== undefined && action.badge > 0 && (
                <span
                  style={{
                    ...styles.quickActionBadge,
                    backgroundColor: action.color,
                  }}
                >
                  {action.badge > 99 ? '99+' : action.badge}
                </span>
              )}
            </span>
            <div style={styles.quickActionContent}>
              <span style={styles.quickActionLabel}>{action.label}</span>
              <span style={styles.quickActionDescription}>{action.description}</span>
            </div>
            <span style={styles.quickActionArrow}>→</span>
          </button>
        ))}
      </div>
    </div>
  );
}

interface ChurnRiskChartProps {
  churnRisk?: { high: number; medium: number; low: number; total: number };
}

/**
 * Churn Risk donut chart component.
 * Displays breakdown of high, medium, and low risk users using Recharts PieChart.
 */
function ChurnRiskChart({ churnRisk }: ChurnRiskChartProps) {
  // Risk level colors matching global styling
  const riskColors = {
    high: '#8B5A50',    // Muted brown
    medium: '#9B7B3C',  // Muted gold/ochre
    low: '#5A8A5E',     // Muted green
  };

  // Use real churn risk values from the API
  const highRisk = churnRisk?.high ?? 0;
  const mediumRisk = churnRisk?.medium ?? 0;
  const lowRisk = churnRisk?.low ?? 0;

  // Calculate total users with churn risk data for accurate percentages
  const totalChurnRiskUsers = highRisk + mediumRisk + lowRisk;

  // Prepare data for donut chart
  const data = [
    { name: 'High Risk', value: highRisk, color: riskColors.high },
    { name: 'Medium Risk', value: mediumRisk, color: riskColors.medium },
    { name: 'Low Risk', value: lowRisk, color: riskColors.low },
  ].filter(item => item.value > 0); // Filter out zero-value segments

  // Handle case where there are no users - graceful fallback when totalUsers is zero
  const hasData = data.length > 0 && totalChurnRiskUsers > 0;

  // Custom tooltip component showing accurate percentage metrics
  const CustomTooltip = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: Array<{ payload: { name: string; value: number; color: string } }>;
  }) => {
    if (active && payload && payload.length > 0) {
      const item = payload[0].payload;
      const percentage = totalChurnRiskUsers > 0 ? ((item.value / totalChurnRiskUsers) * 100).toFixed(1) : '0';
      return (
        <div style={styles.tooltip}>
          <p style={{ ...styles.tooltipTitle, color: item.color }}>{item.name}</p>
          <p style={styles.tooltipValue}>{item.value.toLocaleString()} users</p>
          <p style={{ ...styles.tooltipDate, marginBottom: 0 }}>{percentage}% of total</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div style={styles.chartCard}>
      <div style={styles.chartHeader}>
        <div>
          <h3 style={styles.chartTitle}>Churn Risk Overview</h3>
          <p style={styles.chartSubtitle}>User health breakdown</p>
        </div>
      </div>
      {/* Recharts Pie Chart (styled as donut) */}
      <div style={{ ...styles.donutChartContainer, height: '180px' }}>
        {hasData ? (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={75}
                paddingAngle={2}
                dataKey="value"
                stroke="none"
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <div style={styles.noDataContainer}>
            <span style={{ fontSize: '24px' }}>📊</span>
            <p style={styles.noDataText}>No user data available</p>
          </div>
        )}
      </div>
      {/* Legend */}
      {hasData && (
        <div style={styles.donutLegend}>
          {data.map((item) => (
            <div key={item.name} style={styles.donutLegendItem}>
              <span
                style={{
                  ...styles.donutLegendDot,
                  backgroundColor: item.color,
                }}
              />
              <span style={styles.donutLegendLabel}>{item.name}</span>
              <span style={styles.donutLegendValue}>{item.value.toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    maxWidth: '1200px',
    padding: '32px',
    background: '#FAFAF8',
    minHeight: '100%',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '28px',
  },
  title: {
    fontFamily: '"Sentient", Georgia, serif',
    fontSize: '28px',
    fontWeight: 400,
    color: '#3D3A36',
    marginBottom: '8px',
    margin: 0,
  },
  headerMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginTop: '8px',
  },
  projectBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 14px',
    borderRadius: '100px',
    fontSize: '13px',
    fontWeight: 500,
    color: '#5C564E',
  },
  lastUpdated: {
    fontSize: '13px',
    color: '#9B9285',
  },
  loading: {
    padding: '40px',
    textAlign: 'center',
    color: '#9B9285',
  },
  error: {
    padding: '16px',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    borderRadius: '8px',
  },
  metricsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, 1fr)',
    gap: '16px',
    marginBottom: '24px',
  },
  chartsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
    marginBottom: '24px',
  },
  userGrowthSection: {
    marginBottom: '24px',
  },
  chartCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid rgba(93, 86, 78, 0.08)',
  },
  chartHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '20px',
  },
  chartTitle: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#3D3A36',
    marginBottom: '4px',
    margin: 0,
  },
  chartSubtitle: {
    fontSize: '12px',
    color: '#9B9285',
    margin: 0,
  },
  chartSelect: {
    padding: '6px 12px',
    borderRadius: '6px',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    fontSize: '12px',
    fontFamily: 'inherit',
    background: 'white',
    cursor: 'pointer',
    color: '#5C564E',
  },
  barChartContainer: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    height: '140px',
    paddingTop: '20px',
  },
  barChartColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: 1,
  },
  barChartValue: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#3D3A36',
    marginBottom: '6px',
  },
  barChartLabel: {
    fontSize: '11px',
    color: '#9B9285',
    marginTop: '8px',
  },
  chartInsight: {
    marginTop: '16px',
    padding: '12px',
    borderRadius: '8px',
    background: 'rgba(90, 138, 94, 0.06)',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  chartInsightIcon: {
    fontSize: '14px',
  },
  chartInsightText: {
    fontSize: '12px',
    color: '#5A8A5E',
    margin: 0,
  },
  lineChartContainer: {
    position: 'relative',
    height: '140px',
    marginTop: '10px',
  },
  lineChartLabels: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '8px',
  },
  lineChartLabel: {
    fontSize: '11px',
    color: '#9B9285',
  },
  revenueMrr: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#3D3A36',
    margin: 0,
  },
  revenueChange: {
    fontSize: '11px',
    color: '#5A8A5E',
    margin: 0,
  },
  metricCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '20px',
    border: '1px solid rgba(93, 86, 78, 0.08)',
    transition: 'box-shadow 0.2s ease',
  },
  metricHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '12px',
  },
  metricIcon: {
    fontSize: '20px',
  },
  trendBadge: {
    fontSize: '11px',
    fontWeight: 600,
    padding: '4px 8px',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
  },
  metricValue: {
    fontSize: '28px',
    fontWeight: 600,
    color: '#3D3A36',
    marginBottom: '4px',
    letterSpacing: '-0.5px',
    margin: 0,
  },
  metricLabel: {
    fontSize: '13px',
    color: '#706A62',
    marginBottom: '2px',
    margin: 0,
    marginTop: '4px',
  },
  metricSubtext: {
    fontSize: '11px',
    color: '#9B9285',
    margin: 0,
    marginTop: '2px',
  },
  section: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid rgba(93, 86, 78, 0.08)',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '20px',
  },
  sectionTitle: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#3D3A36',
    margin: 0,
  },
  liveIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    color: '#10b981',
    fontWeight: '500',
  },
  liveDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#10b981',
    animation: 'pulse 2s infinite',
  },
  connectionStatusContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  connectionErrorHint: {
    fontSize: '11px',
    color: '#9B9285',
    fontStyle: 'italic' as const,
  },
  activityList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  activityItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '10px 12px',
    borderRadius: '8px',
    borderLeft: '3px solid transparent',
    transition: 'background-color 0.15s ease',
  },
  activityItemClickable: {
    cursor: 'pointer',
  },
  activityItemInbound: {
    backgroundColor: '#f0fdf4',
    borderLeftColor: '#22c55e',
  },
  activityItemOutbound: {
    backgroundColor: '#eff6ff',
    borderLeftColor: '#3b82f6',
  },
  avatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    fontWeight: '600',
    flexShrink: 0,
  },
  avatarInbound: {
    backgroundColor: '#dcfce7',
    color: '#166534',
  },
  avatarOutbound: {
    backgroundColor: '#dbeafe',
    color: '#1e40af',
    fontSize: '16px',
  },
  activityContent: {
    flex: 1,
    minWidth: 0,
  },
  activityHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '2px',
  },
  activityUserName: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#3D3A36',
  },
  directionBadge: {
    fontSize: '10px',
    fontWeight: '600',
    padding: '2px 6px',
    borderRadius: '4px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.025em',
  },
  directionBadgeInbound: {
    backgroundColor: '#dcfce7',
    color: '#166534',
  },
  directionBadgeOutbound: {
    backgroundColor: '#dbeafe',
    color: '#1e40af',
  },
  activityPreview: {
    fontSize: '13px',
    color: '#706A62',
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-word' as const,
    lineHeight: '1.4',
  },
  activityTime: {
    fontSize: '11px',
    color: '#94a3b8',
    marginLeft: 'auto',
    flexShrink: 0,
  },
  directionIndicator: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: '600',
    flexShrink: 0,
  },
  directionIndicatorInbound: {
    backgroundColor: '#bbf7d0',
    color: '#166534',
  },
  directionIndicatorOutbound: {
    backgroundColor: '#bfdbfe',
    color: '#1e40af',
  },
  empty: {
    padding: '24px',
    textAlign: 'center',
    color: '#94a3b8',
  },
  tooltip: {
    background: 'white',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    borderRadius: '8px',
    padding: '10px 14px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  },
  tooltipTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#3D3A36',
    margin: 0,
    marginBottom: '2px',
  },
  tooltipDate: {
    fontSize: '11px',
    color: '#9B9285',
    margin: 0,
    marginBottom: '6px',
  },
  tooltipValue: {
    fontSize: '12px',
    color: '#6366f1',
    fontWeight: 500,
    margin: 0,
  },
  tooltipHint: {
    fontSize: '10px',
    color: '#9B9285',
    margin: '8px 0 0 0',
    fontStyle: 'italic',
    borderTop: '1px solid rgba(93, 86, 78, 0.1)',
    paddingTop: '6px',
  },
  donutChartContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  donutLegend: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid rgba(93, 86, 78, 0.08)',
  },
  donutLegendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  donutLegendDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  donutLegendLabel: {
    fontSize: '12px',
    color: '#5C564E',
    flex: 1,
  },
  donutLegendValue: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#3D3A36',
  },
  noDataContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    gap: '8px',
  },
  noDataText: {
    fontSize: '13px',
    color: '#9B9285',
    margin: 0,
  },
  quickActionsCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid rgba(93, 86, 78, 0.08)',
    marginBottom: '24px',
  },
  quickActionsHeader: {
    marginBottom: '16px',
  },
  quickActionsTitle: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#3D3A36',
    margin: 0,
    marginBottom: '4px',
  },
  quickActionsSubtitle: {
    fontSize: '12px',
    color: '#9B9285',
    margin: 0,
  },
  quickActionsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '12px',
  },
  quickActionButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 16px',
    background: '#FAFAF8',
    border: '1px solid rgba(93, 86, 78, 0.1)',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    textAlign: 'left' as const,
  },
  quickActionIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    flexShrink: 0,
  },
  quickActionContent: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2px',
    flex: 1,
    minWidth: 0,
  },
  quickActionLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#3D3A36',
  },
  quickActionDescription: {
    fontSize: '11px',
    color: '#9B9285',
  },
  quickActionArrow: {
    fontSize: '14px',
    color: '#9B9285',
    flexShrink: 0,
  },
  quickActionBadge: {
    position: 'absolute' as const,
    top: '-6px',
    right: '-6px',
    minWidth: '18px',
    height: '18px',
    borderRadius: '9px',
    fontSize: '10px',
    fontWeight: 600,
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 4px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.15)',
  },
};

export default DashboardPage;
